import { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import {
  analyzeRecountWorkbook,
  confirmBoxMasterImport,
  confirmIndustrialCostInventoryImport,
  confirmIndustrialCostTechnicalConsumptionRulesImport,
  previewBoxMasterImport,
  previewIndustrialCostInventoryImport,
  previewIndustrialCostTechnicalConsumptionRulesImport,
} from '../services/industrialCostService';
import type {
  BoxMasterKind,
  BoxMasterPreviewResponse,
  RecountWorkbookAnalysis,
} from '../services/industrialCostService';
import type {
  InventoryImportPreviewResponse,
  TechnicalConsumptionImportPreviewResponse,
} from '../types/industrialCost';
import { analyzeFileNormalization } from '../fileNormalization/service';
import {
  changedText,
  countDateFor,
  familiesBreakdownText,
  sheetOptionLabel,
  tablesBreakdownText,
} from '../utils/recountProposal';

// IC-STOCK-10: carga única de los ficheros estructurales (cajas, escandallo y
// recuento). Reutiliza los importadores existentes; solo pide confirmar lo que
// hay que decidir (hoja y fecha del recuento, divisor y "sustituir" del
// escandallo) y los carga en orden: cajas -> escandallo -> recuento.

export type StructuralFileKind = 'box-customer' | 'box-purchase' | 'escandallo' | 'recuento' | 'ignore';
type FileKind = StructuralFileKind;

const KIND_LABELS: Record<FileKind, string> = {
  'box-customer': 'Cajas · personalización por cliente',
  'box-purchase': 'Cajas · caja real por personalización',
  escandallo: 'Escandallo',
  recuento: 'Recuento físico',
  ignore: 'No cargar',
};

const LOAD_ORDER: FileKind[] = ['box-customer', 'box-purchase', 'escandallo', 'recuento'];

const BOX_KIND: Record<'box-customer' | 'box-purchase', BoxMasterKind> = {
  'box-customer': 'customer-personalizations',
  'box-purchase': 'box-purchase-mappings',
};

function normalizeName(name: string) {
  return name
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '');
}

export function detectStructuralFileKind(fileName: string): FileKind {
  const name = normalizeName(fileName);
  if (name.includes('recuento')) return 'recuento';
  if (name.includes('escandallo')) return 'escandallo';
  if (name.includes('personalizacion') || name.includes('mapeo_clientes')) return 'box-customer';
  if (name.includes('cajas_reales') || name.includes('mapeo_cajas')) return 'box-purchase';
  return 'ignore';
}

function errorMessage(error: unknown, fallback: string) {
  const detail = (error as { response?: { data?: { detail?: unknown } } })?.response?.data
    ?.detail;
  if (typeof detail === 'string') return detail;
  if (detail && typeof detail === 'object' && 'message' in detail) {
    return String((detail as { message: unknown }).message);
  }
  return fallback;
}

type Selected = { file: File; kind: FileKind };

type EscandalloState = {
  normalization: { sheet_name: string | null; header_row_index: number };
  preview: TechnicalConsumptionImportPreviewResponse;
  divisor: string;
  replace: boolean;
  blockingReason: string | null;
};

type RecuentoState = {
  analysis: RecountWorkbookAnalysis;
  sheetName: string;
  countDate: string;
  preview: InventoryImportPreviewResponse | null;
  previewError: string | null;
  allowNewMaterials: boolean;
};

type LoadResult = { kind: FileKind; ok: boolean; message: string };

type Props = {
  open: boolean;
  onClose: () => void;
  onFinished: () => void;
  // IND-011: qué ficheros admite esta apertura (recuento solo, o maestras).
  kinds?: Exclude<FileKind, 'ignore'>[];
  title?: string;
};

export default function IndustrialCostStructuralUploadWizard({
  open,
  onClose,
  onFinished,
  kinds,
  title,
}: Props) {
  const allowedKind = (kind: FileKind): FileKind =>
    !kinds || kind === 'ignore' || kinds.includes(kind) ? kind : 'ignore';
  const [step, setStep] = useState<'files' | 'confirm' | 'load'>('files');
  const [selected, setSelected] = useState<Selected[]>([]);
  const [analyzing, setAnalyzing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [boxPreviews, setBoxPreviews] = useState<
    Partial<Record<'box-customer' | 'box-purchase', BoxMasterPreviewResponse>>
  >({});
  const [escandallo, setEscandallo] = useState<EscandalloState | null>(null);
  const [recuento, setRecuento] = useState<RecuentoState | null>(null);

  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<LoadResult[]>([]);
  const [recountPreviewing, setRecountPreviewing] = useState(false);
  // IND-018: texto del año de una hoja «sin año» (se puede teclear entero).
  const [recountYearText, setRecountYearText] = useState('');

  const fileByKind = useMemo(() => {
    const map: Partial<Record<FileKind, File>> = {};
    selected.forEach((item) => {
      if (item.kind !== 'ignore' && !map[item.kind]) map[item.kind] = item.file;
    });
    return map;
  }, [selected]);

  const duplicatedKinds = useMemo(() => {
    const counts: Record<string, number> = {};
    selected.forEach((item) => {
      if (item.kind !== 'ignore') counts[item.kind] = (counts[item.kind] ?? 0) + 1;
    });
    return Object.keys(counts).filter((kind) => counts[kind] > 1);
  }, [selected]);

  function reset() {
    setStep('files');
    setSelected([]);
    setError(null);
    setBoxPreviews({});
    setEscandallo(null);
    setRecuento(null);
    setResults([]);
  }

  function handleClose() {
    if (loading || analyzing) return;
    reset();
    onClose();
  }

  function handleFiles(files: FileList | null) {
    if (!files) return;
    setSelected(
      Array.from(files).map((file) => ({
        file,
        kind: allowedKind(detectStructuralFileKind(file.name)),
      })),
    );
    setError(null);
  }

  async function previewRecount(
    file: File,
    analysis: RecountWorkbookAnalysis,
    sheetName: string,
    countDate: string,
  ) {
    const sheet = analysis.sheets.find((item) => item.sheet_name === sheetName);
    setRecountPreviewing(true);
    try {
      const preview = await previewIndustrialCostInventoryImport(file, countDate || '2000-01-01', {
        sheet_name: sheetName,
        header_row_index: sheet?.header_row_index ?? null,
      });
      setRecuento({
        analysis,
        sheetName,
        countDate,
        preview,
        previewError: null,
        allowNewMaterials: false,
      });
    } catch (err) {
      setRecuento({
        analysis,
        sheetName,
        countDate,
        preview: null,
        previewError: errorMessage(err, 'No se pudo previsualizar el recuento.'),
        allowNewMaterials: false,
      });
    } finally {
      setRecountPreviewing(false);
    }
  }

  async function handleAnalyze() {
    setAnalyzing(true);
    setError(null);
    try {
      const boxes: typeof boxPreviews = {};
      for (const kind of ['box-customer', 'box-purchase'] as const) {
        const file = fileByKind[kind];
        if (file) boxes[kind] = await previewBoxMasterImport(BOX_KIND[kind], file);
      }
      setBoxPreviews(boxes);

      const escandalloFile = fileByKind.escandallo;
      if (escandalloFile) {
        const normalization = await analyzeFileNormalization(
          escandalloFile,
          'technical_consumption_rules',
        );
        const effective = {
          sheet_name: normalization.selected_sheet,
          header_row_index: normalization.header_row_index,
        };
        // Igual que la pantalla del escandallo: se usa el mapeo de columnas
        // guardado la primera vez (p. ej. Codigo_Componente -> material).
        const preview = await previewIndustrialCostTechnicalConsumptionRulesImport(
          escandalloFile,
          normalization.applied_column_mapping ?? undefined,
          undefined,
          undefined,
          effective,
        );
        const unresolvedTypes = preview.detected_material_type_values.filter(
          (item) => !item.is_resolved,
        );
        setEscandallo({
          normalization: effective,
          preview,
          divisor: '100',
          replace: true,
          blockingReason:
            preview.missing_required_fields.length > 0
              ? `Faltan columnas: ${preview.missing_required_fields.join(', ')}.`
              : unresolvedTypes.length > 0
                ? `Tipos sin clasificar: ${unresolvedTypes
                    .map((item) => item.source_value)
                    .join(', ')}.`
                : null,
        });
      } else {
        setEscandallo(null);
      }

      const recuentoFile = fileByKind.recuento;
      if (recuentoFile) {
        const analysis = await analyzeRecountWorkbook(recuentoFile);
        const sheetName = analysis.proposed_sheet_name ?? analysis.sheets[0]?.sheet_name ?? '';
        const countDate =
          analysis.proposed_count_date ?? analysis.sheets[0]?.count_date ?? '';
        await previewRecount(recuentoFile, analysis, sheetName, countDate);
      } else {
        setRecuento(null);
      }

      setStep('confirm');
    } catch (err) {
      setError(errorMessage(err, 'No se pudieron analizar los ficheros.'));
    } finally {
      setAnalyzing(false);
    }
  }

  async function handleLoad() {
    setStep('load');
    setLoading(true);
    const outcome: LoadResult[] = [];
    for (const kind of LOAD_ORDER) {
      const file = fileByKind[kind];
      if (!file) continue;
      try {
        if (kind === 'box-customer' || kind === 'box-purchase') {
          if (!boxPreviews[kind]?.can_confirm) {
            outcome.push({ kind, ok: false, message: 'No se carga: el fichero tiene errores.' });
            continue;
          }
          const result = await confirmBoxMasterImport(BOX_KIND[kind], file);
          outcome.push({ kind, ok: true, message: `${result.imported_count} filas cargadas.` });
        } else if (kind === 'escandallo' && escandallo) {
          if (!(Number(escandallo.divisor) > 0)) {
            outcome.push({
              kind,
              ok: false,
              message: 'No se carga: el divisor debe ser un número mayor que 0.',
            });
            continue;
          }
          if (escandallo.blockingReason) {
            outcome.push({
              kind,
              ok: false,
              message: `No se carga: ${escandallo.blockingReason} Usa la pantalla del escandallo.`,
            });
            continue;
          }
          const typeMapping: Record<string, string | null> = {};
          escandallo.preview.detected_material_type_values.forEach((item) => {
            typeMapping[item.source_value] = item.resolved_material_type ?? null;
          });
          const result = await confirmIndustrialCostTechnicalConsumptionRulesImport(
            file,
            escandallo.preview.auto_mapping,
            {},
            {},
            typeMapping,
            Number(escandallo.divisor),
            escandallo.normalization,
            escandallo.replace,
          );
          outcome.push({
            kind,
            ok: true,
            message:
              `${result.rows_imported} reglas, ${result.rows_pending_created} pendientes` +
              (result.replace_existing
                ? `, ${result.rules_deactivated ?? 0} reglas antiguas desactivadas.`
                : '.') +
              ((result.rows_skipped_test_article ?? 0) > 0
                ? ` ${result.rows_skipped_test_article} líneas informativas (9999) ignoradas.`
                : ''),
          });
        } else if (kind === 'recuento' && recuento?.preview) {
          const blocked = recountBlockingReason(recuento);
          if (blocked) {
            outcome.push({ kind, ok: false, message: `No se carga: ${blocked}` });
            continue;
          }
          const sheet = recuento.analysis.sheets.find(
            (item) => item.sheet_name === recuento.sheetName,
          );
          const result = await confirmIndustrialCostInventoryImport(
            file,
            recuento.countDate,
            recuento.preview.auto_mapping,
            {},
            { sheet_name: recuento.sheetName, header_row_index: sheet?.header_row_index ?? null },
          );
          outcome.push({
            kind,
            ok: true,
            message:
              `${result.rows_imported} filas con fecha ${result.count_date}` +
              (result.materials_marked_external || result.rows_skipped_unresolved_material
                ? `; materiales sin reconocer: ${result.rows_skipped_unresolved_material} filas sin cargar.`
                : '.') +
              ((result.rows_skipped_test_article ?? 0) > 0
                ? ` ${result.rows_skipped_test_article} líneas informativas (9999) ignoradas.`
                : '') +
              // IND-018: desglose y materiales que ya no están (puestos a 0).
              (result.raw_materials_imported != null
                ? ` ${result.raw_materials_imported} materias primas · ${result.packaging_imported ?? 0} embalajes` +
                  ((result.other_imported ?? 0) > 0 ? ` · ${result.other_imported} más.` : '.')
                : '') +
              ((result.zeroed_materials ?? []).length > 0
                ? ` Puestos a 0 por no estar ya en el recuento de esta fecha: ${(result.zeroed_materials ?? []).join(', ')}.`
                : '') +
              // IND-023: comparación con el stock calculado.
              (result.difference_snapshot?.message ? ` ${result.difference_snapshot.message}` : ''),
          });
        } else if (kind === 'recuento') {
          outcome.push({ kind, ok: false, message: 'No se carga: no se pudo previsualizar.' });
        }
      } catch (err) {
        outcome.push({ kind, ok: false, message: errorMessage(err, 'Error al cargar.') });
      }
      setResults([...outcome]);
    }
    setResults(outcome);
    setLoading(false);
    onFinished();
  }

  function recountBlockingReason(state: RecuentoState): string | null {
    if (!state.sheetName) return 'no hay hoja seleccionada.';
    if (!/^\d{4}-\d{2}-\d{2}$/.test(state.countDate)) return 'la fecha de recuento no es válida.';
    if (!state.preview) return 'no se pudo previsualizar.';
    if (state.preview.missing_required_fields.length > 0) {
      return `faltan columnas: ${state.preview.missing_required_fields.join(', ')}.`;
    }
    const quantityColumn = String(state.preview.auto_mapping.quantity ?? '').trim().toUpperCase();
    if (quantityColumn !== 'ACTUAL') {
      return `la cantidad se leería de la columna «${state.preview.auto_mapping.quantity ?? '—'}» y no de ACTUAL; usa la pantalla de recuento.`;
    }
    const unresolved = state.preview.detected_material_refs.filter((item) => !item.is_resolved);
    if (unresolved.length > 0 && !state.allowNewMaterials) {
      return `${unresolved.length} materiales no existen en el catálogo y se darían de alta; márcalo si es correcto o resuélvelos en la pantalla de recuento.`;
    }
    return null;
  }

  const recountSheet = recuento?.analysis.sheets.find(
    (item) => item.sheet_name === recuento.sheetName,
  );
  const unresolvedRecountMaterials =
    recuento?.preview?.detected_material_refs.filter((item) => !item.is_resolved) ?? [];
  const nothingSelected = Object.keys(fileByKind).length === 0;

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
      <DialogTitle>{title ?? 'Cargar ficheros estructurales'}</DialogTitle>

      <DialogContent dividers>
        <Stack spacing={2}>
          {error ? <Alert severity="error">{error}</Alert> : null}

          {step === 'files' ? (
            <>
              <Alert severity="info" variant="outlined">
                {kinds
                  ? `Selecciona: ${kinds.map((kind) => KIND_LABELS[kind]).join(', ')}. `
                  : 'Selecciona a la vez el recuento, el escandallo y los dos Excel de cajas. '}
                Se reconocen por el nombre; si alguno no, elige qué es.
              </Alert>
              <Button variant="outlined" component="label" sx={{ alignSelf: 'flex-start' }}>
                Elegir ficheros
                <input
                  hidden
                  multiple
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  onChange={(event) => {
                    handleFiles(event.target.files);
                    event.target.value = '';
                  }}
                />
              </Button>
              {selected.map((item, index) => (
                <Stack key={`${item.file.name}-${index}`} direction="row" spacing={1} alignItems="center">
                  <Typography sx={{ flex: 1, fontSize: 13 }}>{item.file.name}</Typography>
                  <TextField
                    select
                    size="small"
                    value={item.kind}
                    sx={{ minWidth: 300 }}
                    onChange={(event) =>
                      setSelected((current) =>
                        current.map((entry, position) =>
                          position === index
                            ? { ...entry, kind: event.target.value as FileKind }
                            : entry,
                        ),
                      )
                    }
                  >
                    {(Object.keys(KIND_LABELS) as FileKind[])
                      .filter((kind) => allowedKind(kind) === kind)
                      .map((kind) => (
                      <MenuItem key={kind} value={kind}>
                        {KIND_LABELS[kind]}
                      </MenuItem>
                    ))}
                  </TextField>
                </Stack>
              ))}
              {duplicatedKinds.length > 0 ? (
                <Alert severity="warning">
                  Hay varios ficheros del mismo tipo; se usará el primero de cada uno.
                </Alert>
              ) : null}
            </>
          ) : null}

          {step === 'confirm' ? (
            <>
              {(['box-customer', 'box-purchase'] as const).map((kind) => {
                const preview = boxPreviews[kind];
                if (!preview) return null;
                return (
                  <Box key={kind} sx={{ border: '1px solid rgba(148,163,184,0.2)', borderRadius: 2, p: 1.5 }}>
                    <Typography sx={{ fontWeight: 800 }}>{KIND_LABELS[kind]}</Typography>
                    <Stack direction="row" spacing={1} sx={{ my: 1 }} flexWrap="wrap" useFlexGap>
                      <Chip size="small" color="success" label={`${preview.valid_rows_count} filas a cargar`} />
                      {preview.blocking_errors.length ? (
                        <Chip size="small" color="error" label={`${preview.blocking_errors.length} errores`} />
                      ) : null}
                      {preview.warnings.length ? (
                        <Chip size="small" color="warning" label={`${preview.warnings.length} avisos`} />
                      ) : null}
                    </Stack>
                    {[...preview.blocking_errors, ...preview.warnings].map((issue, index) => (
                      <Typography key={index} sx={{ fontSize: 12, opacity: 0.85 }}>
                        • {issue.message}
                      </Typography>
                    ))}
                    {!preview.can_confirm ? (
                      <Alert severity="error" sx={{ mt: 1 }}>
                        No se cargará: corrige los errores en el Excel.
                      </Alert>
                    ) : null}
                  </Box>
                );
              })}

              {escandallo ? (
                <Box sx={{ border: '1px solid rgba(148,163,184,0.2)', borderRadius: 2, p: 1.5 }}>
                  <Typography sx={{ fontWeight: 800 }}>{KIND_LABELS.escandallo}</Typography>
                  <Typography sx={{ fontSize: 12, opacity: 0.85, my: 0.5 }}>
                    {escandallo.preview.coverage_summary.total_rows} filas · hoy hay{' '}
                    {escandallo.preview.existing_active_rules_count ?? '—'} reglas activas.
                  </Typography>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
                    <TextField
                      size="small"
                      type="number"
                      label="Cantidad expresada por cada"
                      value={escandallo.divisor}
                      onChange={(event) =>
                        setEscandallo({ ...escandallo, divisor: event.target.value })
                      }
                      sx={{ maxWidth: 240 }}
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={escandallo.replace}
                          onChange={(event) =>
                            setEscandallo({ ...escandallo, replace: event.target.checked })
                          }
                        />
                      }
                      label="Sustituir escandallo completo"
                    />
                  </Stack>
                  {escandallo.replace ? (
                    <Typography sx={{ fontSize: 12, opacity: 0.85 }}>
                      Para cada producto del fichero se desactivan (no se borran) sus reglas que
                      no estén en él; los productos que no vienen no se tocan, y los pendientes
                      de importaciones anteriores se descartan.
                    </Typography>
                  ) : null}
                  {!(Number(escandallo.divisor) > 0) ? (
                    <Alert severity="error" sx={{ mt: 1 }}>
                      El divisor debe ser un número mayor que 0.
                    </Alert>
                  ) : null}
                  {escandallo.blockingReason ? (
                    <Alert severity="error" sx={{ mt: 1 }}>
                      No se cargará desde aquí: {escandallo.blockingReason} Usa la pantalla del
                      escandallo para resolverlo.
                    </Alert>
                  ) : null}
                </Box>
              ) : null}

              {recuento ? (
                <Box sx={{ border: '1px solid rgba(148,163,184,0.2)', borderRadius: 2, p: 1.5 }}>
                  <Typography sx={{ fontWeight: 800 }}>{KIND_LABELS.recuento}</Typography>
                  <Typography sx={{ fontSize: 12, opacity: 0.85, my: 0.5 }}>
                    {recuento.analysis.rule}
                  </Typography>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ my: 1 }}>
                    <TextField
                      select
                      size="small"
                      label="Hoja (columna ACTUAL)"
                      value={recuento.sheetName}
                      sx={{ minWidth: 280 }}
                      onChange={(event) => {
                        const sheet = recuento.analysis.sheets.find(
                          (item) => item.sheet_name === event.target.value,
                        );
                        const file = fileByKind.recuento;
                        setRecountYearText('');
                        if (sheet && file) {
                          void previewRecount(file, recuento.analysis, sheet.sheet_name, sheet.count_date ?? '');
                        }
                      }}
                    >
                      {recuento.analysis.sheets.map((sheet) => (
                        <MenuItem key={sheet.sheet_name} value={sheet.sheet_name}>
                          {sheetOptionLabel(sheet)}
                        </MenuItem>
                      ))}
                    </TextField>
                    {/* IND-018: hoja sin año: el usuario elige el año. */}
                    {recountSheet?.year_missing ? (
                      <TextField
                        size="small"
                        type="number"
                        label="Año de la hoja"
                        value={recountYearText}
                        inputProps={{ min: 2000, max: 2100 }}
                        onChange={(event) => {
                          setRecountYearText(event.target.value);
                          const year = Number(event.target.value);
                          if (year >= 2000 && year <= 2100) {
                            setRecuento({ ...recuento, countDate: countDateFor(recountSheet.month, year) });
                          }
                        }}
                        sx={{ width: 140 }}
                      />
                    ) : null}
                    <TextField
                      size="small"
                      type="date"
                      label="Fecha de recuento"
                      value={recuento.countDate}
                      InputLabelProps={{ shrink: true }}
                      onChange={(event) =>
                        setRecuento({ ...recuento, countDate: event.target.value })
                      }
                    />
                  </Stack>
                  {/* IND-018: propuesta clara, desglose por tabla y si el recuento está completo. */}
                  {recountSheet ? (
                    <Stack spacing={0.4} sx={{ mb: 1 }}>
                      <Typography sx={{ fontSize: 13, fontWeight: 800 }}>
                        {sheetOptionLabel(recountSheet)}
                      </Typography>
                      {familiesBreakdownText(recountSheet.families) || tablesBreakdownText(recountSheet.tables) ? (
                        <Typography sx={{ fontSize: 12, opacity: 0.9 }}>
                          {familiesBreakdownText(recountSheet.families) || tablesBreakdownText(recountSheet.tables)}
                        </Typography>
                      ) : null}
                      {changedText(recountSheet) ? (
                        <Typography sx={{ fontSize: 12, opacity: 0.9 }}>{changedText(recountSheet)}</Typography>
                      ) : null}
                      {(recuento.analysis.discarded_newer_sheets ?? []).map((item) => (
                        <Typography key={item.sheet_name} sx={{ fontSize: 12, opacity: 0.8 }}>
                          {`No se propone ${item.sheet_name}: ${item.reason}.`}
                        </Typography>
                      ))}
                    </Stack>
                  ) : null}
                  {(recuento.analysis.warnings ?? []).map((warning) => (
                    <Alert key={`${warning.code}-${warning.sheet_name}`} severity="warning" sx={{ mb: 1 }}>
                      {warning.message}
                    </Alert>
                  ))}
                  {recountSheet && !recountSheet.year_missing && !recountSheet.actual_filled ? (
                    <Alert severity="warning">
                      La columna ACTUAL de {recountSheet.sheet_name} parece una copia (aún sin
                      contar). Normalmente se usa la hoja del mes anterior.
                    </Alert>
                  ) : null}
                  {recountSheet?.next_month_check ? (
                    recountSheet.next_month_check.mismatches_count === 0 ? (
                      <Alert severity="success" variant="outlined" sx={{ mt: 1 }}>
                        Coincide con MES ANTERIOR de {recountSheet.next_sheet_name} en los{' '}
                        {recountSheet.next_month_check.compared_materials} materiales.
                      </Alert>
                    ) : (
                      <Alert severity="warning" sx={{ mt: 1 }}>
                        {recountSheet.next_month_check.mismatches_count} materiales no coinciden con
                        MES ANTERIOR de {recountSheet.next_sheet_name}:{' '}
                        {recountSheet.next_month_check.mismatches
                          .slice(0, 5)
                          .map((item) => `${item.material_code} (${item.actual ?? 0} vs ${item.next_month_previous ?? 0})`)
                          .join(', ')}
                        . Revisa el Excel antes de cargar.
                      </Alert>
                    )
                  ) : null}
                  {recuento.previewError ? (
                    <Alert severity="error" sx={{ mt: 1 }}>
                      {recuento.previewError}
                    </Alert>
                  ) : null}
                  {recountPreviewing ? (
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
                      <CircularProgress size={16} />
                      <Typography sx={{ fontSize: 12 }}>Leyendo la hoja…</Typography>
                    </Stack>
                  ) : null}
                  {recuento.preview && !recountPreviewing ? (
                    <Stack spacing={0.5} sx={{ mt: 1 }}>
                      <Typography sx={{ fontSize: 12, opacity: 0.85 }}>
                        Columnas: material «{recuento.preview.auto_mapping.material ?? '—'}» ·
                        cantidad «{recuento.preview.auto_mapping.quantity ?? '—'}» ·{' '}
                        {recuento.preview.coverage_summary.rows_with_material_ref} filas con material
                        {recountSheet ? ` (el análisis encontró ${recountSheet.materials_count} materiales)` : ''}.
                      </Typography>
                      {unresolvedRecountMaterials.length > 0 ? (
                        <Alert severity="warning">
                          {unresolvedRecountMaterials.length} materiales no existen en el catálogo y se
                          darán de alta como materiales nuevos:{' '}
                          {unresolvedRecountMaterials
                            .slice(0, 10)
                            .map((item) => item.source_value)
                            .join(', ')}
                          {unresolvedRecountMaterials.length > 10 ? '…' : ''}. Si alguno es un código
                          mal escrito, corrígelo en el Excel o resuélvelo en la pantalla de recuento.
                          <FormControlLabel
                            sx={{ display: 'block', mt: 0.5 }}
                            control={
                              <Checkbox
                                checked={recuento.allowNewMaterials}
                                onChange={(event) =>
                                  setRecuento({ ...recuento, allowNewMaterials: event.target.checked })
                                }
                              />
                            }
                            label="Dar de alta estos materiales y cargar el recuento"
                          />
                        </Alert>
                      ) : null}
                      {recountBlockingReason(recuento) ? (
                        <Alert severity="error">No se cargará: {recountBlockingReason(recuento)}</Alert>
                      ) : null}
                    </Stack>
                  ) : null}
                </Box>
              ) : null}
            </>
          ) : null}

          {step === 'load' ? (
            <Stack spacing={1}>
              {loading ? (
                <Stack direction="row" spacing={1} alignItems="center">
                  <CircularProgress size={18} />
                  <Typography>Cargando en orden: cajas → escandallo → recuento…</Typography>
                </Stack>
              ) : null}
              {results.map((result) => (
                <Alert key={result.kind} severity={result.ok ? 'success' : 'error'} variant="outlined">
                  <strong>{KIND_LABELS[result.kind]}:</strong> {result.message}
                </Alert>
              ))}
              {!loading ? (
                <Alert severity="info">
                  Terminado. Revisa el resultado en «Revisa tus ficheros», en esta misma página.
                </Alert>
              ) : null}
            </Stack>
          ) : null}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={loading || analyzing}>
          {step === 'load' && !loading ? 'Cerrar' : 'Cancelar'}
        </Button>
        {step === 'files' ? (
          <Button
            variant="contained"
            onClick={() => void handleAnalyze()}
            disabled={nothingSelected || analyzing}
          >
            {analyzing ? 'Analizando…' : 'Analizar'}
          </Button>
        ) : null}
        {step === 'confirm' ? (
          <Button
            variant="contained"
            color="success"
            onClick={() => void handleLoad()}
            disabled={recountPreviewing || loading}
          >
            Cargar en orden
          </Button>
        ) : null}
      </DialogActions>
    </Dialog>
  );
}
